export const ORDERING_PRODUCT_NAME = "Ordering.co";
export const DOCUMENT_TITLE_SEPARATOR = " — ";

const translation = (namespace, key) => ({ namespace, key });

const exactRouteTitles = new Map([
  ["/", translation("navigation", "sidebar.overview")],
  ["/overview", translation("navigation", "sidebar.overview")],
  ["/ai-agents", translation("navigation", "sidebar.aiAgents")],
  ["/orders", translation("navigation", "sidebar.orders")],
  [
    "/analyze/conversations",
    translation("navigation", "sidebar.conversations"),
  ],
  ["/analyze/voice-calls", translation("navigation", "sidebar.voiceCalls")],
  ["/analyze/reports", translation("navigation", "sidebar.reports")],
  ["/users", translation("navigation", "sidebar.customers")],
  ["/discounts", translation("navigation", "sidebar.discounts")],
  ["/growth/cart-recovery", translation("navigation", "sidebar.cartRecovery")],
  ["/growth/cart-recovery/new", translation("cartRecovery", "create.title")],
  ["/growth/link-in-bio", translation("navigation", "sidebar.linkInBio")],
  ["/growth/meta-ads", translation("navigation", "sidebar.metaAds")],
  ["/growth/meta-ads/connect", translation("metaAds", "connect.title")],
  ["/growth/meta-ads/create", translation("navigation", "sidebar.metaAds")],
  ["/growth/meta-ads/success", translation("metaAds", "success.title")],
  ["/growth/meta-ads/performance", translation("metaAds", "performance.title")],
  ["/menu/stores", translation("navigation", "sidebar.stores")],
  [
    "/menu/stores/create/address",
    translation("stores", "addressSelection.header.title"),
  ],
  ["/menu/stores/create/form", translation("stores", "actions.createBusiness")],
  ["/menu/products", translation("navigation", "sidebar.products")],
  ["/menu/brands", translation("navigation", "sidebar.brands")],
  ["/integrations/pos", translation("navigation", "sidebar.posSystems")],
  [
    "/integrations/menu-importers",
    translation("navigation", "sidebar.menuImporters"),
  ],
  [
    "/integrations/sales-channels",
    translation("navigation", "sidebar.salesChannels"),
  ],
  [
    "/integrations/delivery-services",
    translation("navigation", "sidebar.deliveryServices"),
  ],
  [
    "/delivery/drivers-managers",
    translation("navigation", "sidebar.driversManagers"),
  ],
  ["/delivery/groups", translation("navigation", "sidebar.deliveryGroups")],
  [
    "/delivery/groups/create",
    translation("delivery", "createGroup.header.title"),
  ],
  ["/settings/basic", translation("navigation", "sidebar.general")],
  [
    "/settings/online-payments",
    translation("navigation", "sidebar.onlinePayments"),
  ],
  [
    "/settings/agent-translations",
    translation("navigation", "sidebar.agentTranslations"),
  ],
  ["/settings/admins", translation("navigation", "sidebar.admins")],
  ["/support", translation("navigation", "sidebar.support")],
  ["/account", translation("account", "header.title")],
  ["/campaigns", translation("campaigns", "header.title")],
  ["/campaigns/new", translation("campaigns", "createPage.header.title")],
]);

const dynamicRouteTitles = [
  [/^\/orders\/[^/]+$/, translation("navigation", "sidebar.orders")],
  [/^\/users\/[^/]+$/, translation("navigation", "sidebar.customers")],
  [
    /^\/analyze\/conversations\/[^/]+$/,
    translation("navigation", "sidebar.conversations"),
  ],
  [/^\/discounts\/[^/]+$/, translation("navigation", "sidebar.discounts")],
  [/^\/menu\/stores\/[^/]+$/, translation("navigation", "sidebar.stores")],
  [/^\/menu\/products\/[^/]+$/, translation("navigation", "sidebar.products")],
  [/^\/menu\/brands\/[^/]+$/, translation("navigation", "sidebar.brands")],
  [
    /^\/integrations\/delivery-services\/[^/]+$/,
    translation("navigation", "sidebar.deliveryServices"),
  ],
  [
    /^\/delivery\/drivers-managers\/[^/]+$/,
    translation("navigation", "sidebar.driversManagers"),
  ],
  [
    /^\/delivery\/groups\/[^/]+$/,
    translation("navigation", "sidebar.deliveryGroups"),
  ],
  [/^\/settings\/admins\/[^/]+$/, translation("navigation", "sidebar.admins")],
  [
    /^\/growth\/cart-recovery\/[^/]+$/,
    translation("navigation", "sidebar.cartRecovery"),
  ],
  [
    /^\/growth\/link-in-bio\/[^/]+$/,
    translation("navigation", "sidebar.linkInBio"),
  ],
  [
    /^\/growth\/meta-ads\/edit\/[^/]+$/,
    translation("metaAds", "create.editBreadcrumbLabel"),
  ],
  [
    /^\/growth\/meta-ads\/ad-sets\/edit\/[^/]+$/,
    translation("metaAds", "create.editAdSetBreadcrumbLabel"),
  ],
  [
    /^\/growth\/meta-ads\/ads\/edit\/[^/]+$/,
    translation("metaAds", "create.editAdBreadcrumbLabel"),
  ],
  [/^\/campaigns\/[^/]+$/, translation("campaigns", "header.title")],
];

export const publicRouteTitle = (pathname) => {
  if (pathname === "/password/forgot") {
    return translation("auth", "forgotPasswordTitle");
  }
  if (pathname === "/reset-password") {
    return translation("auth", "resetPasswordTitle");
  }
  if (pathname === "/download-app") {
    return translation("downloadApp", "title");
  }
  return null;
};

export const routeTitle = (pathname) => {
  const exact = exactRouteTitles.get(pathname);
  if (exact) return exact;

  return (
    dynamicRouteTitles.find(([pattern]) => pattern.test(pathname))?.[1] ?? null
  );
};

export const statusTitle = (status) => {
  if (status === "loading") {
    return translation("common", "states.loading");
  }
  if (status === "error") {
    return translation("common", "states.error");
  }
  if (status === "forbidden") {
    return { ...translation("common", "states.error"), prefix: "403 · " };
  }
  if (status === "not-found") {
    return { ...translation("common", "states.error"), prefix: "404 · " };
  }
  return null;
};

export const formatDocumentTitle = (pageTitle) =>
  `${pageTitle}${DOCUMENT_TITLE_SEPARATOR}${ORDERING_PRODUCT_NAME}`;

export const resolveDocumentTitle = (descriptor, t) => {
  if (!descriptor) return null;
  const localized = t(descriptor.key, { ns: descriptor.namespace });
  return formatDocumentTitle(`${descriptor.prefix ?? ""}${localized}`);
};

export const setDocumentTitle = (descriptor, t) => {
  const title = resolveDocumentTitle(descriptor, t);
  if (title) document.title = title;
  return title;
};
